import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { WidenService } from '@services/widen.service';
import { ArticleCollectionContent } from '@marketing/insights/insights.interface';
import { Logger } from '@utils/logger';
import { LinkService } from '@services/link.service';
import { AppStateService, SegmentService } from '@services';
import { Subject, Subscription } from 'rxjs';
import { ResponsiveService, WindowService } from '@frk/eds-components';
import { takeUntil } from 'rxjs/operators';
import { WINDOW } from '@ng-web-apis/common';
import { ArticleService } from '@marketing/article/services/article.service';

/**
 * Logger
 */
const logger = Logger.getLogger('ArticleCollectionComponent');

/**
 * The Article Collection component
 *
 * Selector: `ft-article-collection`
 *
 * Exported as: `ArticleCollectionComponent`
 */
@Component({
  selector: 'ft-article-collection',
  templateUrl: './article-collection.component.html',
})
export class ArticleCollectionComponent
  extends AbstractBaseComponent
  implements OnInit, OnDestroy {
  // Component fields
  content: ArticleCollectionContent;
  translationLabels: any;

  public relatedContentHeaderTypeLabel: string;
  compactdet = false;
  public showButtons = false;
  sidebarOffsetTop: number;
  sidebarHeight: number;
  segmentTags: string;
  audienceExclusions?: Array<string>;
  public showSegment = false;
  // Element Refs
  @ViewChild('sidebar') sidebar: ElementRef;

  // Observables
  isMobile$: Subscription;
  isMobile: boolean;

  /**
   * SIM logo resource bundle object
   */
  simLogoResourceBundle: object;

  // Private fields
  private unsubscribe$: Subject<void> = new Subject<void>();

  /**
   * Constructor
   */
  constructor(
    @Inject(WINDOW) private windowRef: Window,
    private appStateService: AppStateService,
    private widenService: WidenService,
    private ctaLinkService: LinkService,
    private responsiveService: ResponsiveService,
    private windowService: WindowService,
    private cdr: ChangeDetectorRef,
    private segmentService: SegmentService,
    private articleService: ArticleService
  ) {
    super();
    this.segmentTags = this.segmentService.getCurrentSegment()?.insightAudiences;
  }

  /**
   * Angular Init Hook
   */
  ngOnInit(): void {
    super.ngOnInit();
    this.setContentData();
  }

  private setContentData(): void {
    this.content = this.document?.getData<ArticleCollectionContent>();
    // Responsive behaviours
    this.isMobile$ = this.responsiveService
      .isMobile$()
      ?.subscribe((isMobile) => (this.isMobile = isMobile));

    if (this.isMobile) {
      this.showButtons = true;
      this.compactdet = true;
    }

    this.windowService
      .getScrollObs$(100)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        if (this.isMobile) {
          return;
        }
        const windowScroll = this.windowRef.pageYOffset;
        this.sidebarOffsetTop = this.sidebar.nativeElement.offsetTop;
        this.sidebarHeight = this.sidebar.nativeElement.offsetHeight;
        this.showButtons =
          windowScroll > this.sidebarOffsetTop + this.sidebarHeight;
        this.cdr.detectChanges();
      });

    // Load translations from resource bundles
    this.translationLabels = this.component?.getModels()[
      'ft.core.insightsLibrary'
    ];
    this.relatedContentHeaderTypeLabel = this.component?.getModels()[
      'ft.core.article-related-content'
    ];
    this.simLogoResourceBundle = this.component?.getModels()[
      'ft.core.sim-logos'
    ];
    // Article Related Content
    if (this.content?.relatedContent?.length) {
      this.articleService.getRelatedContent(
        this.segmentTags,
        this.content,
        this.page
      );
    }

    if (this.content?.audienceExclusions) {
      this.showSegment = this.articleService.isSegmentTagAvailable(
        this.segmentTags,
        JSON.stringify(this.content?.audienceExclusions)
      );
    } else {
      this.showSegment = false;
    }

    // Set banner breadcrumbs
    this.setBreadcrumbItems();

    // Format banner image
    if (this.content?.bannerImage) {
      this.content.bannerImage = this.widenService.getWidenAssetUrl(
        this.content.bannerImage.widenDocument
      );
    }

    // Format cards in main body & right rail
    this.formatBlocks(this.content?.content);
    this.formatBlocks(this.content?.rightRail);
  }
  /**
   * Fomrat the card compound for display
   * @param card cardCompound object
   */
  formatBlocks(blocks: any): void {
    for (const block of blocks) {
      // Check should we show it
      if (block.targeting) {
        block.isAllowed = this.articleService.isSegmentTagAvailable(
          this.segmentTags,
          block.targeting
        );
      } else {
        block.isAllowed = true;
      }
      // Card compound array
      if (block.cardCompound) {
        for (const card of block.cardCompound) {
          // Format image
          if (card.image) {
            card.image = this.widenService.getWidenAssetUrl(
              card.image.widenAsset
            );
          }
          // Format link
          if (card.link?.linkCollection?.length) {
            card.link = card.link.linkCollection[0];
            card.href = this.ctaLinkService.getCTALink(
              this.page,
              card.link,
              this.appStateService?.getHomePageUrl()
            );
          }
        }
      }
      // End Card formatting
    }
  }

  /**
   * Angular hook
   */
  ngOnDestroy() {
    this.isMobile$?.unsubscribe();
  }
}
